import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  Stack,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  Percent as PercentIcon,
  Receipt as ReceiptIcon,
  CalendarToday as CalendarIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import InvoiceService from '../../services/invoice.service';
import ClientService from '../../services/client.service';
import http from '../../http-common';
import { formatCurrencyWithCode, formatCurrencySync, initializeCurrency, getCurrencySymbolByCode, getCurrencySymbolSync } from '../../utils/currencyUtils';
import VisitService from '../../services/visit.service';
import { useTranslation } from '../../hooks/useTranslation';

const InvoiceForm = ({ invoice, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    header: {
      client_id: '',
      visit_id: '',
      name: '',
      address: '',
      phone: '',
      email: '',
      date: new Date().toISOString().split('T')[0],
      nit: '',
      authorization: '',
      anulada: false,
      reimpresion: false
    },
    details: [
      {
        cantidad: 1,
        descripcion: '',
        precio: 0,
        descuento: 0
      }
    ]
  });

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const { t } = useTranslation();
  const [clientCurrency, setClientCurrency] = useState(null);
  const [systemCurrency, setSystemCurrency] = useState('USD');

  const getCurrencyFromClient = (client) => {
    if (!client) return null;
    
    // Check if client has fieldValues
    if (client.fieldValues && Array.isArray(client.fieldValues)) {
      // Look for a field with label containing "currency" (case insensitive)
      const currencyField = client.fieldValues.find(fv => 
        fv.field && 
        (fv.field.label?.toLowerCase().includes('currency') || 
         fv.field.label?.toLowerCase() === 'currency')
      );
      
      if (currencyField && currencyField.value) {
        return currencyField.value.toUpperCase();
      }
    }
    
    // Also check field_values (alternative naming)
    if (client.field_values && Array.isArray(client.field_values)) {
      const currencyField = client.field_values.find(fv => 
        fv.field && 
        (fv.field.label?.toLowerCase().includes('currency') || 
         fv.field.label?.toLowerCase() === 'currency')
      );
      
      if (currencyField && currencyField.value) {
        return currencyField.value.toUpperCase();
      }
    }
    
    return null;
  };

  const updateClientCurrency = (clientId, clientsList) => {
    if (!clientId) {
      setClientCurrency(null);
      return;
    }
    
    const client = clientsList.find(c => c.id === clientId);
    if (client) {
      const currency = getCurrencyFromClient(client);
      setClientCurrency(currency);
    } else {
      setClientCurrency(null);
    }
  };

  const loadClients = async () => {
    try {
      const response = await ClientService.getClients();
      let loadedClients = [];
      if (response.data.success) {
        loadedClients = response.data.data;
      } else {
        loadedClients = response.data?.data || [];
      }
      setClients(loadedClients);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };
 

  useEffect(() => {
    const loadSystemCurrency = async () => {
      await initializeCurrency();
      // Get the system currency after initialization
      // Fetch it directly from settings
      try {
        const response = await http.get('/settings');
        const currency = response.data.settings?.currency || 'USD';
        setSystemCurrency(currency);
      } catch (error) {
        console.error('Error loading system currency:', error);
        setSystemCurrency('USD');
      }
    };
    
    loadSystemCurrency();
    loadClients();
    if (invoice) {
      setFormData({
        header: {
          client_id: invoice.client_id || '',
          visit_id: invoice.visit_id || '',
          name: invoice.name || '',
          address: invoice.address || '',
          phone: invoice.phone || '',
          email: invoice.email || '',
          date: invoice.date || new Date().toISOString().split('T')[0],
          nit: invoice.nit || '',
          authorization: invoice.authorization || '',
          anulada: invoice.anulada || false,
          reimpresion: invoice.reimpresion || false
        },
        details: invoice.idetails?.map(detail => ({
          cantidad: detail.cantidad,
          descripcion: detail.descripcion,
          precio: detail.precio,
          descuento: detail.descuento || 0
        })) || [
          {
            cantidad: 1,
            descripcion: '',
            precio: 0,
            descuento: 0
          }
        ]
      });
    }
  }, [invoice]);

  // Update currency when client_id or clients change
  useEffect(() => {
    if (formData.header.client_id && clients.length > 0) {
      updateClientCurrency(formData.header.client_id, clients);
    } else if (!formData.header.client_id) {
      setClientCurrency(null);
    }
  }, [formData.header.client_id, clients]);

  const handleHeaderChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      header: {
        ...prev.header,
        [field]: value
      }
    }));
    
    // Update currency when client changes
    if (field === 'client_id') {
      updateClientCurrency(value, clients);
    }
  };

  const handleDetailChange = (index, field, value) => {
    const newDetails = [...formData.details];
    newDetails[index][field] = value;
    setFormData(prev => ({
      ...prev,
      details: newDetails
    }));
  };

  const addDetail = () => {
    setFormData(prev => ({
      ...prev,
      details: [
        ...prev.details,
        {
          cantidad: 1,
          descripcion: '',
          precio: 0,
          descuento: 0
        }
      ]
    }));
  };

  const removeDetail = (index) => {
    if (formData.details.length > 1) {
      const newDetails = formData.details.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        details: newDetails
      }));
    }
  };

  const calculateTotals = () => {
    const subtotal = formData.details.reduce((sum, detail) => {
      return sum + (detail.cantidad * detail.precio);
    }, 0);

    const totalDiscount = formData.details.reduce((sum, detail) => {
      const lineTotal = detail.cantidad * detail.precio;
      return sum + (lineTotal * (detail.descuento / 100));
    }, 0);

    const total = subtotal - totalDiscount;

    return { subtotal, totalDiscount, total };
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      // Prevent saving if invoice is certified
      if (invoice && invoice.fel_status === 'certificado' && invoice.fel_uuid) {
        setError('Cannot edit a certified invoice. Please cancel the invoice first if you need to make changes.');
        return;
      }

      // Client-side validation
      if (!formData.header.name || !formData.header.name.trim()) {
        setError('Business Name is required');
        return;
      }
      
      if (!formData.header.address || !formData.header.address.trim()) {
        setError('Business Address is required');
        return;
      }
      
      if (!formData.header.phone || !formData.header.phone.trim()) {
        setError('Phone is required');
        return;
      }
      
      if (!formData.header.email || !formData.header.email.trim()) {
        setError('Email is required');
        return;
      }
      
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.header.email)) {
        setError('Please enter a valid email address');
        return;
      }
      
      if (!formData.header.nit || !formData.header.nit.trim()) {
        setError('NIT/Tax ID is required');
        return;
      }
      
      if (!formData.header.date) {
        setError('Date is required');
        return;
      }
      
      if (formData.details.length === 0) {
        setError('At least one invoice item is required');
        return;
      }
      
      if (formData.details.some(detail => !detail.descripcion || detail.descripcion.trim() === '' || detail.precio <= 0)) {
        setError('Please fill in all detail fields with valid values (description and price > 0)');
        return;
      }

      const invoiceData = {
        header: formData.header,
        details: formData.details
      };

      if (invoice) {
        await InvoiceService.updateInvoice(invoice.id, invoiceData);
      } else {
        await InvoiceService.createInvoice(invoiceData);
      }

      onSave();
    } catch (error) {
      // Handle validation errors from backend
      if (error.response?.status === 422 && error.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        // Format validation errors into a readable message
        const errorMessages = Object.keys(validationErrors).map(key => {
          const messages = validationErrors[key];
          return Array.isArray(messages) ? messages.join(', ') : messages;
        });
        setError(`Validation failed: ${errorMessages.join('; ')}`);
      } else {
        // Show the actual error message from backend if available
        const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to save invoice';
        setError(errorMessage);
      }
      console.error('Error saving invoice:', error);
      console.error('Error response:', error.response?.data);
    } finally {
      setSaving(false);
    }
  };

  const { subtotal, totalDiscount, total } = calculateTotals();

  const formatCurrency = (amount) => {
    // Use client's currency if available, otherwise fall back to system currency
    if (clientCurrency) {
      return formatCurrencyWithCode(amount, clientCurrency);
    }
    return formatCurrencySync(amount);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ReceiptIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
          <Typography variant="h4" component="h1" fontWeight="bold">
            {invoice ? t ('invoices.editInvoice') : t ('invoices.createNewinvoice')}
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          {invoice ? 'Update invoice details' :  t ('invoices.msginfoInvoice')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Invoice Header */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <BusinessIcon sx={{ mr: 1 }} />
                {t ('invoices.infoinvoice')}
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t ('clients.client')}</InputLabel>
                    <Select
                      value={formData.header.client_id}
                      onChange={(e) => handleHeaderChange('client_id', e.target.value)}
                      label="Client"
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="">
                        <em>No client selected</em>
                      </MenuItem>
                      {clients.map((client) => (
                        <MenuItem key={client.id} value={client.id}>
                          {client.name} ({client.email})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label= {t ('invoices.businessname')}
                    value={formData.header.name}
                    onChange={(e) => handleHeaderChange('name', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BusinessIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="NIT/Tax ID"
                    value={formData.header.nit}
                    onChange={(e) => handleHeaderChange('nit', e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Do not include &quot;-&quot;">
                            <InfoIcon sx={{ color: 'text.secondary', fontSize: 20, cursor: 'help' }} />
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t ('common.date')}
                    type="date"
                    value={formData.header.date}
                    onChange={(e) => handleHeaderChange('date', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t ('invoices.businessaddress')}
                    multiline
                    rows={2}
                    value={formData.header.address}
                    onChange={(e) => handleHeaderChange('address', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t ('common.phone')}
                    value={formData.header.phone}
                    onChange={(e) => handleHeaderChange('phone', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t ('common.email')}
                    type="email"
                    value={formData.header.email}
                    onChange={(e) => handleHeaderChange('email', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>

                

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t ('common.authorization')}
                    value={formData.header.authorization}
                    disabled
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mt: 2 }}>
                    <Chip
                      label={formData.header.anulada ? t ('invoices.Cancelled') : t ('common.active')}
                      color={formData.header.anulada ? 'error' : 'success'}
                      onClick={() => handleHeaderChange('anulada', !formData.header.anulada)}
                      clickable
                    />
                    <Chip
                      label={formData.header.reimpresion ? t ('common.reprint'): 'Original'}
                      color={formData.header.reimpresion ? 'info' : 'default'}
                      onClick={() => handleHeaderChange('reimpresion', !formData.header.reimpresion)}
                      clickable
                    />
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Invoice Details */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                  <ReceiptIcon sx={{ mr: 1 }} />
                  {t ('invoices.itemsinvoice') }
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addDetail}
                  sx={{ borderRadius: 2 }}
                >
                  {t ('common.addItem') }
                </Button>
              </Box>

              <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table>
                  <TableHead sx={{ backgroundColor: 'grey.50' }}>
                    <TableRow>
                      <TableCell><strong>{t ('common.qty')}</strong></TableCell>
                      <TableCell><strong>{t ('common.description')}</strong></TableCell>
                      <TableCell align="right"><strong>{t ('common.price')}</strong></TableCell>
                      <TableCell align="right"><strong>{t ('common.discount')}%</strong></TableCell>
                      <TableCell align="right"><strong>{t ('common.total')}</strong></TableCell>
                      <TableCell align="center"><strong>{t ('common.actions')}</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.details.map((detail, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <TextField
                            type="number"
                            value={detail.cantidad}
                            onChange={(e) => handleDetailChange(index, 'cantidad', parseInt(e.target.value) || 0)}
                            size="small"
                            sx={{ width: 80 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            value={detail.descripcion}
                            onChange={(e) => handleDetailChange(index, 'descripcion', e.target.value)}
                            placeholder={t ('common.itemDescription')}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            value={detail.precio}
                            onChange={(e) => handleDetailChange(index, 'precio', parseFloat(e.target.value) || 0)}
                            size="small"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  {clientCurrency ? getCurrencySymbolByCode(clientCurrency) : getCurrencySymbolByCode(systemCurrency)}
                                </InputAdornment>
                              ),
                            }}
                            sx={{ width: 110 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            value={detail.descuento}
                            onChange={(e) => handleDetailChange(index, 'descuento', parseFloat(e.target.value) || 0)}
                            size="small"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                                    %
                                  </Typography>
                                </InputAdornment>
                              ),
                            }}
                            sx={{ width: 110 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency((detail.cantidad * detail.precio) - ((detail.cantidad * detail.precio) * (detail.descuento / 100)))}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => removeDetail(index)}
                            disabled={formData.details.length === 1}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Totals */}
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Paper sx={{ p: 2, minWidth: 300 }}>
                  <Typography variant="h6" gutterBottom> {t ('invoices.summaryInvoice')}</Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>Subtotal:</Typography>
                    <Typography>{formatCurrency(subtotal)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>{t ('common.discount')}:</Typography>
                    <Typography color="error">-{formatCurrency(totalDiscount)}</Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6"><strong>Total:</strong></Typography>
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      {formatCurrency(total)}
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={onCancel}
              disabled={saving}
              sx={{ borderRadius: 2 }}
            >
             {t ('common.cancel')}
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                }
              }}
            >
              {saving ? 'Saving...' : t ('common.save')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default InvoiceForm;
